import { useEffect, useState } from "react";

/**
 * Which day it is, and staying right about it — O-20, bug Z-10.
 *
 * This is its own module because "today" was computed once when the entry
 * screen mounted, and a phone does not remount at midnight. A site manager
 * closed the app on the evening of 13.08. with three confirmed entries and
 * opened it on 14.08.; the PWA came back from the background with the same
 * component still mounted, so the day list still said "Danas" and still meant
 * the 13th. The screen was showing yesterday's entries, correctly cached and
 * correctly filtered, under today's heading — the entry screen for the new day
 * was never offered, and the crew read as already done.
 *
 * Neither the server nor the cache was wrong: `GET /sati` filters by date and
 * the device copy is keyed by `gradiliste|datum`. The only thing that was
 * stale was the screen's idea of what day it is.
 */

const DANI = ["Ned", "Pon", "Uto", "Sri", "Čet", "Pet", "Sub"];

/**
 * How far back a day may still be entered.
 *
 * NN 55/24 čl. 15 gives seven days from the day being recorded, so the window
 * is today plus the six behind it. Older than that is not a form to fill in
 * but a breach to explain, and personnel resolve it through the register.
 */
export const RASPON_DANA = 7;

/** The business day is Zagreb's date, regardless of the device timezone. */
export function danas(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Zagreb",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

export function naslovDana(iso: string): string {
  const datum = new Date(`${iso}T00:00:00`);
  const dan = DANI[datum.getDay()] ?? "";
  return `${dan}, ${String(datum.getDate()).padStart(2, "0")}.${String(
    datum.getMonth() + 1,
  ).padStart(2, "0")}.`;
}

/** The days that may still be entered, today first. */
export function dostupniDani(
  danasIso: string,
  count = RASPON_DANA,
): { iso: string; naziv: string }[] {
  return Array.from({ length: count }, (_, pomak) => {
    const datum = new Date(`${danasIso}T12:00:00Z`);
    datum.setUTCDate(datum.getUTCDate() - pomak);
    const iso = datum.toISOString().slice(0, 10);
    const naziv =
      pomak === 0 ? "Danas" : pomak === 1 ? "Jučer" : naslovDana(iso);
    return { iso, naziv };
  });
}

/**
 * Today, re-read whenever this device could have crossed midnight unnoticed.
 *
 * `visibilitychange` and `focus` are the two moments a backgrounded PWA comes
 * back, which is the case that produced Z-10. The interval covers the other
 * one: a phone left awake on a site through midnight fires neither event, and
 * a night shift is ordinary here — the entry screen already records shifts
 * that run past midnight, so a manager sitting on this screen at 00:05 is not
 * a hypothetical.
 *
 * A minute is far finer than the thing being watched, and the work is one
 * string comparison; the state only changes on the one tick a day where the
 * date actually differs, so nothing re-renders in between.
 */
const PROVJERA_MS = 60_000;

export function useDanas(): string {
  const [danasIso, setDanasIso] = useState(danas);

  useEffect(() => {
    const provjeri = () => {
      // Compared against the current state rather than assigned blindly: an
      // unconditional set would re-render the entry screen every minute and
      // restart every fetch that hangs off the day.
      setDanasIso((prethodni) => {
        const sada = danas();
        return sada === prethodni ? prethodni : sada;
      });
    };

    const naVidljivost = () => {
      if (document.visibilityState === "visible") provjeri();
    };

    document.addEventListener("visibilitychange", naVidljivost);
    window.addEventListener("focus", provjeri);
    const tajmer = window.setInterval(provjeri, PROVJERA_MS);

    return () => {
      document.removeEventListener("visibilitychange", naVidljivost);
      window.removeEventListener("focus", provjeri);
      window.clearInterval(tajmer);
    };
  }, []);

  return danasIso;
}
